import { useEffect, useState } from "react";
import api from "../lib/api";
import { Shield, RefreshCw, Copy, Check, Tablet } from "lucide-react";
import { toast } from "sonner";

export default function KioskPairingCard() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/settings/kiosk-code");
      setCode(res.data.code || "");
    } catch {
      /* silent */
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    if (!window.confirm("Regenerate the kiosk pairing code? All currently paired tablets will need to re-pair with the new code.")) return;
    setRegenerating(true);
    try {
      const res = await api.post("/settings/kiosk-code/regenerate");
      setCode(res.data.code || "");
      toast.success("New code generated — re-pair tablets now");
    } catch {
      toast.error("Failed to regenerate");
    }
    setRegenerating(false);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Code copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const formatted = code ? code.slice(0, 3) + " " + code.slice(3) : "• • • • • •";

  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-5 text-white flex items-center gap-5" data-testid="kiosk-pairing-card">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center flex-shrink-0">
        <Tablet className="w-5 h-5 text-white" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 font-['Manrope']">Kiosk Pairing Code</p>
          <Shield className="w-2.5 h-2.5 text-white/40" />
        </div>
        <p className="font-mono text-2xl font-bold text-white tracking-[0.3em] tabular-nums" data-testid="kiosk-pairing-code">
          {loading ? "……" : formatted}
        </p>
        <p className="text-[11px] text-white/40 mt-1 font-['Manrope']">Use this on the tablet at <span className="font-mono text-white/60">/clock</span> to pair</p>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button onClick={copyCode} disabled={!code}
          data-testid="kiosk-pairing-copy"
          title="Copy code"
          className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white flex items-center justify-center transition-colors disabled:opacity-30">
          {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
        </button>
        <button onClick={regenerate} disabled={regenerating}
          data-testid="kiosk-pairing-regenerate"
          title="Regenerate code"
          className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white flex items-center justify-center transition-colors disabled:opacity-30">
          <RefreshCw className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`} />
        </button>
      </div>
    </div>
  );
}
